import { SEARCH_LIMIT, formatFileSize, escapeHtml, detectMeta, encodeFilePath } from '../../../modules/files/file-list.utils.js';
import { getMatchConditions } from '../../../modules/search/search-conditions.js';
import { addDateHighlightPatterns } from '../../../modules/filters/filter-manager/highlight-patterns.js';
import { highlightText } from '../../../modules/search/search-conditions.js';
import { fuseSearch } from '../../../modules/search/fuse-search.js';
import { tgSendTooltip, tgLinkTooltip } from './tg-send.js';
import { favHas, favAddTooltip, favRemoveTooltip } from '../../favorites/favorites-state.js';

let containerSelector = '#file-list-container';

/**
 * Translate document type key
 * @param {string} type - Document type (e.g., 'HCOB', 'PAB', 'LECT')
 * @returns {string} Translated type name
 */
function translateDocumentType(type) {
	if (!type) return '';
	
	// Try TranslationRegistry first (new system)
	if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
		const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
		const key = `MaterialType_${type}`;
		const translation = window.TranslationRegistry.translate(key, lang);
		if (translation !== key) {
			return translation;
		}
	}
	
	// Try LanguageManager.translate (fallback)
	if (window.LanguageManager && typeof window.LanguageManager.translate === 'function') {
		const key = `MaterialType_${type}`;
		const translation = window.LanguageManager.translate(key);
		if (translation !== key) {
			return translation;
		}
	}
	
	// Fallback to type itself
	return type;
}

/**
 * Get document type description (for title attribute)
 * @param {string} type - Document type (e.g., 'HCOB', 'PAB', 'LECT')
 * @returns {string} Translated description or empty string
 */
function getDocumentTypeDescription(type) {
	if (!type) return '';
	
	// Try TranslationRegistry first (new system)
	if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
		const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
		const key = `MaterialType_${type}_desc`;
		const translation = window.TranslationRegistry.translate(key, lang);
		if (translation !== key && translation) {
			return translation;
		}
	}
	
	// Try LanguageManager.translate (fallback)
	if (window.LanguageManager && typeof window.LanguageManager.translate === 'function') {
		const key = `MaterialType_${type}_desc`;
		const translation = window.LanguageManager.translate(key);
		if (translation !== key && translation) {
			return translation;
		}
	}
	
	// Fallback to empty string
	return '';
}

async function checkSummariesExist(files) {
	const hasSummaries = new Set();
	try {
		const { openDatabase, closeDatabase, getSummariesByKeys } = await import('../../storage/idb-files.js');
		const db = await openDatabase();
		const fileKeys = files.map(file => file.n || file.name || '');
		const summaries = await getSummariesByKeys(db, fileKeys);
		summaries.forEach((summary, idx) => {
			if (summary) hasSummaries.add(fileKeys[idx]);
		});
		closeDatabase(db);
	} catch (error) {
		console.error('Error checking summaries:', error);
	}
	return hasSummaries;
}

export async function renderFiles(files, append = false) {
	const debugMode = (new URLSearchParams(window.location.search)).get('debug') || '0';
	
	const container = $(containerSelector);
	if (!container.length) {
		console.error('[render] ❌ Container not found:', containerSelector);
		return;
	}
	
	if (debugMode === '1') {
		console.log('[FileList Render] renderFiles called:', { filesLength: files ? files.length : 0, append, containerSelector });
	}
	
	// No blur effect - direct replacement without loading state
	
	if (!files || files.length === 0) { 
		if (!append) {
			// Replace content in one step to avoid showing empty state
			if (debugMode === '1') {
				console.log('[FileList Render] Rendering empty state');
			}
			container.html('<div class="p-3 text-center text-muted">No results by Name/Path</div>');
		}
		return; 
	}
	
	const fileKeysWithSummaries = await checkSummariesExist(files);
	
	// Get current search query for highlighting
	// CRITICAL: Always check input field first - it's the source of truth
	// Only use cached values if input field is empty (for initial load)
	let searchQuery = '';
	let highlightConditions = null;
	try {
		// First, check the actual input field - this is the source of truth
		const $searchInput = window.jQuery ? window.jQuery('#fnd_searchbox, input[name="sq"], input[name="search"]').first() : null;
		if ($searchInput && $searchInput.length) {
			searchQuery = ($searchInput.val() || '').trim();
		}
		
		// Only use cached values if input field is empty (for initial page load)
		if (!searchQuery) {
			if (window.filterController && window.filterController.activeFilters) {
				searchQuery = (window.filterController.activeFilters.searchQuery || '').trim();
			}
			if (!searchQuery && window.$ && typeof window.$.urlParam === 'function') {
				searchQuery = (window.$.urlParam('sq') || '').trim();
			}
		}
		
		// Only generate highlight conditions if we have a non-empty query
		if (searchQuery && searchQuery.length > 0) {
			try {
				highlightConditions = getMatchConditions(searchQuery, false);
				// Add date highlight patterns for highlighting dates in filenames/paths
				if (highlightConditions && typeof addDateHighlightPatterns === 'function') {
					highlightConditions = addDateHighlightPatterns(highlightConditions, searchQuery);
				}
			} catch(_) { 
				// If getMatchConditions fails, don't highlight
				highlightConditions = null;
			}
		} else {
			// Explicitly set to null if query is empty
			highlightConditions = null;
		}
	} catch(_) { 
		// On any error, don't highlight
		highlightConditions = null;
	}
	
	let html = '';
	let previousType = null;
	let startIndex = 0;

	// If append mode (infinite scroll), get last type from existing items and calculate start index
	// For regular pagination (append=false), startIndex should be 0 as container is cleared
	if (append) {
		const existingItems = container.find('.file-item');
		startIndex = existingItems.length;
		const lastItem = existingItems.last();
		if (lastItem.length) {
			const lastTypeLabel = lastItem.find('.type-label').text();
			previousType = lastTypeLabel || null;
		}
	} else {
		// For regular pagination, startIndex should be calculated based on page number
		// First check if start_index is provided in window (from API response) - this is the most reliable
		if (window.currentPaginationStartIndex !== undefined && window.currentPaginationStartIndex !== null) {
			startIndex = window.currentPaginationStartIndex;
		} else {
			// Check for pagination data in window or container
			const paginationInfo = window.paginationController || window.currentPaginationInfo;
			if (paginationInfo && paginationInfo.currentPage && paginationInfo.itemsPerPage) {
				// Calculate startIndex based on page number: (page - 1) * itemsPerPage
				startIndex = (paginationInfo.currentPage - 1) * paginationInfo.itemsPerPage;
			} else {
				// Fallback: check URL for page parameter (used by fulltext search API)
				const urlParams = new URLSearchParams(window.location.search);
				// Try page first (used by fulltext search API), then fnd_page as fallback
				const page = parseInt(urlParams.get('page') || urlParams.get('fnd_page') || '1', 10);
				// Use items_per_page from API response if available, otherwise default to 10 (matches backend)
				const itemsPerPage = window.currentPaginationItemsPerPage || 10;
				startIndex = (page - 1) * itemsPerPage;
			}
		}
	}
	
	files.forEach((file, idx) => {
		// Index is 1-based for display (first item is 1, not 0)
		const index = startIndex + idx + 1;
		const fileName = file.n || file.name || 'Unknown';
		
		// DEBUG: Log UTF-8 encoding check for German umlauts
		if (fileName.toLowerCase().includes('zus') && fileName.toLowerCase().includes('tzliche')) {
			console.log('[FileList Render] UTF-8 DEBUG - Before processing:', {
				index: index,
				fileName: fileName,
				fileNameLength: fileName.length,
				charAt3: fileName.charAt(3),
				charAt4: fileName.charAt(4),
				charAt5: fileName.charAt(5),
				charCodeAt3: fileName.charCodeAt(3),
				charCodeAt4: fileName.charCodeAt(4),
				charCodeAt5: fileName.charCodeAt(5),
				includesA: fileName.includes('Ä'),
				includesAumlaut: fileName.includes('\u00C4'),
				bytes: Array.from(new TextEncoder().encode(fileName)).slice(0, 20),
				rawFile: file
			});
		}
		const rawSize = (typeof file.s !== 'undefined')
			? (Number(file.s) * 1000)
			: (typeof file.size !== 'undefined' ? Number(file.size) : 0);
		const fileSize = formatFileSize(rawSize);
		const sizeParts = String(fileSize).split(' ');
		const sizeNum = sizeParts[0] || '';
		const sizeUnit = sizeParts[1] || '';
		const unitClass = sizeUnit ? `file-size__unit--${sizeUnit.toLowerCase()}` : '';
		const unitMap = { 'KB': 'KB', 'MB': 'MB', 'GB': 'GB' };
		const sizeUnitDisplay = unitMap[sizeUnit] || sizeUnit;
		const unitFirst = String(sizeUnitDisplay).slice(0,1);
		const unitSecond = String(sizeUnitDisplay).slice(1,2);
		const filePath = file.p || file.path || '';
		const showPath = !!filePath;
		const hasOnline = !!(file.o === 1 || file.online === 1);
		const meta = detectMeta(filePath, fileName);
		const extLower = (meta.ext || '').toLowerCase();
		// Normalize DOCX to DOC for display and CSS class
		const extDisplay = meta.ext && meta.ext.toUpperCase() === 'DOCX' ? 'DOC' : meta.ext;
		const extClass = meta.ext && meta.ext.toUpperCase() === 'DOCX' ? 'doc' : extLower;
		const isMP3 = extLower === 'mp3';
		const isPDF = extLower === 'pdf';
		
		// Calculate file size in bytes
		const fileSizeBytes = (typeof file.s !== 'undefined')
			? (Number(file.s) * 1000) // file.s is in KB
			: (typeof file.size !== 'undefined' ? Number(file.size) : 0);
		const maxPreviewSizeBytes = 100 * 1000 * 1000; // 100 MB
		
		// Preview available for:
		// - MP3 files (always, regardless of size)
		// - PDF files (if size is 0/unknown OR under 100MB)
		// - Online files (if size is 0/unknown OR under 100MB)
		// Note: For online files, if size is unknown (0), we always allow preview
		const isWithinSizeLimit = fileSizeBytes === 0 || fileSizeBytes <= maxPreviewSizeBytes;
		
		// Check if user is authenticated (guest)
		const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
		const isGuest = !isAuthenticated;
		const isOT = !!(file.ot || file.ot_level || (typeof file.category === 'string' && file.category.toUpperCase() === 'OT'));
		const isPublic = !!(file.pu === 1);
		
		// Check country restrictions
		const restrictionMode = file._restrictionMode || (file.r ? 'block' : null); // Default to block if r exists but mode not set
		const isRestricted = restrictionMode && (restrictionMode === 'block' || restrictionMode === 'warn');
		
		// For guests, non-public files (without p=1) are not accessible
		const isAccessible = !isGuest || isPublic;

		// Send-to-Telegram rail: authenticated + accessible + not restricted.
		// Files above the Bot API 50 MB document cap still send — the bot delivers
		// a download-link message instead of the document (tooltip says so).
		const tgOversize = fileSizeBytes > 50 * 1024 * 1024;
		const tgTitle = tgOversize ? tgLinkTooltip() : tgSendTooltip();
		const tgIconCls = '';
		const tgIconAttr = tgOversize ? ' data-tg-oversize="1"' : '';
		
		// Separate checks for clarity:
		// - MP3: always previewable
		// - PDF: previewable if within size limit
		// - Online files: previewable if within size limit (or size unknown)
		// - Only previewable if file is accessible (user can actually view it)
		// - Not previewable if file is restricted (block or warn mode)
		const canPreview = isAccessible && !isRestricted && (isMP3 || (isPDF && isWithinSizeLimit) || (hasOnline));
		
		const typeClass = (meta.type ? ` doc-type doc-type--${meta.type.toLowerCase()}` : '') + (isOT ? ' doc-type--ot' : '');
		const showTypeLabel = !!meta.type && meta.type !== previousType;
		const translatedType = meta.type ? translateDocumentType(meta.type) : '';
		const typeDescription = meta.type ? getDocumentTypeDescription(meta.type) : '';
		const typeTitle = typeDescription ? ` title="${escapeHtml(typeDescription)}"` : '';
		const formatIconMap = { text: 'bi-body-text', scan: 'bi-image-alt', audio: 'bi-magnetic-tape-reel', video: 'bi-camera-video' };
		const formatTitleMap = { text: 'Text', scan: 'Scan', audio: 'Audio', video: 'Video' };
		const format = meta.format || 'text';
		const formatIconClass = formatIconMap[format] || 'bi-file-earmark';
		const formatTitle = formatTitleMap[format] || 'File';
		const isAudio = format === 'audio' || isMP3;
		const previewIconClass = isAudio ? 'bi-volume-up-fill' : 'bi-eye';
		const previewTitle = isAudio ? 'Preview audio' : 'Preview document';

		const fileKey = fileName;
		const hasSummary = fileKeysWithSummaries.has(fileKey);
		
		// Prepare minimal file data for preview (save in DOM to avoid searching arrays)
		// Use base64 encoding to safely store JSON in HTML attribute
		const fileDataForPreview = {
			n: file.n || file.name || '',
			p: file.p || file.path || '',
			s: file.s, // size in KB (for 100MB limit check)
			o: file.o || file.online || 0,
			ot: file.ot || file.ot_level || 0,
			cat: file.category || '',
			_url: file._url || '' // optional, for version parameter
		};
		const fileDataJson = JSON.stringify(fileDataForPreview);
		// Encode to base64 for safe storage in HTML attribute
		const fileDataEncoded = btoa(encodeURIComponent(fileDataJson));
		
		// Apply highlighting to file name and path if search query exists
		let highlightedFileName = escapeHtml(fileName);
		let highlightedFilePath = showPath ? escapeHtml(filePath) : '';
		
		// DEBUG: Log UTF-8 encoding check after escapeHtml
		if (fileName.toLowerCase().includes('zus') && fileName.toLowerCase().includes('tzliche')) {
			console.log('[FileList Render] UTF-8 DEBUG - After escapeHtml:', {
				fileName: fileName,
				highlightedFileName: highlightedFileName,
				highlightedFileNameLength: highlightedFileName.length,
				includesA: highlightedFileName.includes('Ä'),
				includesAumlaut: highlightedFileName.includes('\u00C4'),
				includesAEntity: highlightedFileName.includes('&Auml;'),
				includesAHex: highlightedFileName.includes('&#196;')
			});
		}
		
		// Проверить, есть ли Fuse.js matches для этого файла (нечеткий поиск)
		const hasFuseMatches = file._fuseMatches && Array.isArray(file._fuseMatches) && file._fuseMatches.length > 0;
		const fuseQuery = file._fuseQuery || searchQuery;
		
		if (debugMode === '1' && idx < 3) {
			if (hasFuseMatches) {
				console.log('[FileList Render] ✅ Fuse matches:', {
					fileName: fileName.substring(0, 50),
					matchesCount: file._fuseMatches.length,
					matchesKeys: file._fuseMatches.map(m => m.key)
				});
			} else if (searchQuery) {
				console.log('[FileList Render] ❌ No Fuse matches:', {
					fileName: fileName.substring(0, 50),
					fileKeys: Object.keys(file).filter(k => k.startsWith('_')).join(', ')
				});
			}
		}
		
		if (hasFuseMatches && window.FuseSearch && typeof window.FuseSearch.highlightMatches === 'function') {
			// Использовать подсветку через Fuse.js matches (более точная для нечеткого поиска)
			try {
				// Fuse.js ищет в нормализованных полях, поэтому передаем оригинальные ключи ('n', 'p')
				// функция сама найдет соответствующие нормализованные поля (_norm_n, _norm_p)
				const nameHighlighted = window.FuseSearch.highlightMatches(fileName, fuseQuery, file._fuseMatches, 'n');
				highlightedFileName = nameHighlighted || escapeHtml(fileName);
				
				if (showPath) {
					const pathHighlighted = window.FuseSearch.highlightMatches(filePath, fuseQuery, file._fuseMatches, 'p');
					highlightedFilePath = pathHighlighted || escapeHtml(filePath);
				}
				
				if (debugMode === '1' && idx < 3) {
					const wasHighlighted = nameHighlighted !== escapeHtml(fileName);
					console.log('[FileList Render] Applied Fuse.js highlighting:', {
						fileName: fileName.substring(0, 80),
						nameHighlighted: wasHighlighted,
						originalFileName: fileName.substring(0, 80),
						highlightedFileName: nameHighlighted.substring(0, 100),
						hasMatches: file._fuseMatches.length,
						matchesKeys: file._fuseMatches.map(m => m.key),
						query: fuseQuery,
						result: wasHighlighted ? '✅ HIGHLIGHTED' : '❌ NOT HIGHLIGHTED',
						note: 'If NOT HIGHLIGHTED, check FuseSearch._highlightFuzzy logs'
					});
				}
			} catch(e) {
				if (debugMode === '1') {
					console.warn('[FileList Render] Fuse.js highlight failed, falling back to regular highlight:', e);
				}
				// Fallback to regular highlighting
				if (highlightConditions && highlightConditions.length > 0) {
					try {
						highlightedFileName = highlightText(fileName, highlightConditions, searchQuery || '');
						if (showPath) {
							highlightedFilePath = highlightText(filePath, highlightConditions, searchQuery || '');
						}
					} catch(_) {
						highlightedFileName = escapeHtml(fileName);
						highlightedFilePath = showPath ? escapeHtml(filePath) : '';
					}
				}
			}
		} else if (highlightConditions && highlightConditions.length > 0) {
			// Обычная подсветка через highlightText (для точного поиска)
			try {
				// Pass queryOriginal to highlightText for date pattern recognition
				highlightedFileName = highlightText(fileName, highlightConditions, searchQuery || '');
				if (showPath) {
					highlightedFilePath = highlightText(filePath, highlightConditions, searchQuery || '');
				}
				
				// DEBUG: Log UTF-8 encoding check after highlightText
				if (fileName.toLowerCase().includes('zus') && fileName.toLowerCase().includes('tzliche')) {
					console.log('[FileList Render] UTF-8 DEBUG - After highlightText:', {
						fileName: fileName,
						highlightedFileName: highlightedFileName,
						includesA: highlightedFileName.includes('Ä'),
						includesAumlaut: highlightedFileName.includes('\u00C4')
					});
				}
			} catch(_) {
				highlightedFileName = escapeHtml(fileName);
				highlightedFilePath = showPath ? escapeHtml(filePath) : '';
			}
		}

		// Check if path has highlighted matches (contains <b> tags)
		const pathHasHighlights = showPath && highlightedFilePath.includes('<b>');

		// Favorites star rail: same visibility rules as the tg rail. The rel path is
		// the favorites identity key (matches the server's decoded file_path).
		const favP = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
		const favN = (file.n || file.name || '');
		const favRelPath = `${favP}${favP ? '/' : ''}${favN}`;
		const favOn = favHas(favRelPath);
		const favTitle = favOn ? favRemoveTooltip() : favAddTooltip();

		// Calculate file URL - use server-provided _url if available (includes version)
		let fileHref = '';
		if (!isAccessible) {
			// For guests, non-public files link to login page with current URL as next parameter
			const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
			fileHref = `/login?next=${currentUrl}`;
		} else if (hasOnline) {
			// For files with online version, always use /online/ endpoint (opens in browser, not download)
			const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
			const n = (file.n || file.name || '');
			const rel = `${p}${p ? '/' : ''}${n}`;
			// If file._url exists and contains version parameter, preserve it
			if (file._url && file._url.includes('?v=')) {
				const versionMatch = file._url.match(/[?&]v=([^&]+)/);
				if (versionMatch) {
					fileHref = `/online/${encodeFilePath(rel)}?v=${versionMatch[1]}`;
				} else {
					fileHref = `/online/${encodeFilePath(rel)}`;
				}
			} else {
				fileHref = `/online/${encodeFilePath(rel)}`;
			}
		} else if (file._url) {
			// Use server-provided URL with version parameter (for files without online version)
			fileHref = file._url;
		} else if (isRestricted && restrictionMode === 'warn') {
			// For warn mode, link goes to download endpoint (will show warning form)
			const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
			const n = (file.n || file.name || '');
			const rel = `${p}${p ? '/' : ''}${n}`;
			fileHref = `/library/${encodeFilePath(rel)}`;
		} else if (isOT) {
			const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
			const n = (file.n || file.name || '');
			const rel = `${p}${p ? '/' : ''}${n}`;
			fileHref = `/library/${encodeFilePath(rel)}?ot=1`;
		} else {
			const p = (file.p || file.path || '').replace(/^\//, '').replace(/\/+/, '/');
			const n = (file.n || file.name || '');
			const rel = `${p}${p ? '/' : ''}${n}`;
			fileHref = `/library/${encodeFilePath(rel)}`;
		}

		// Right-rails: format folded into the meta column as a 3rd text row
		// (LANG / EXT / FORMAT), size num-over-unit to its left; no format icon rail.
		// (Promoted from DEV-only to prod 2026-07-20 at the owner's request.)
		const _metaTitle = meta.lang ? meta.lang + (meta.ext ? ' / ' + meta.ext : '') : (meta.ext ? meta.ext : 'File metadata');
		const _langCell = meta.lang
			? `<span class="meta-rail__row meta-rail__row--muted">${meta.lang}</span>`
			: '<span class="meta-rail__row meta-rail__row--muted">&nbsp;</span>';
		const _extCell = meta.ext
			? `<span class="meta-rail__row meta-rail__row--ext ext--${extClass}">${extDisplay}</span>`
			: '<span class="meta-rail__row meta-rail__row--ext">&nbsp;</span>';
		const _sizeInner = `<div class="size-rail__content"><span class="size-rail__num">${sizeNum}</span><span class="size-rail__unit ${unitClass}">${unitFirst}${unitSecond ? `<span class="size-rail__unit--minor">${unitSecond}</span>` : ''}</span></div>`;
		const _otCell = isOT ? '<span class="ot-lock meta-rail__ot" title="OT"><i class="bi bi-lock-fill"></i></span>' : '';
		// Lead cluster (row start): the meta column (LANG/EXT/FORMAT). Size is rendered
		// at the row END (right-rails). A fixed preview slot is added in the template.
		const metaBlock = `
				<div class="meta-rail icon-rail meta-rail--stacked" title="${_metaTitle} / ${formatTitle}">
					<div class="meta-rail__content">
						${_langCell}
						${_extCell}
						<span class="meta-rail__row meta-rail__row--format meta-rail__row--muted">${_otCell}${escapeHtml(formatTitle)}</span>
					</div>
				</div>`;
		const sizeBlock = `<div class="size-rail icon-rail size-rail--stacked" title="File size: ${fileSize}">${_sizeInner}</div>`;
		// Preview eye: a hover-only darkened square badge overlaid at the right edge of
		// the name zone (only for previewable rows — no reserved slot on the rest).
		const previewOverlay = canPreview
			? `<div class="preview-rail preview-rail--overlay" title="${previewTitle}"><i class="bi ${previewIconClass} preview-icon" title="${previewTitle}" data-action="preview" data-file-index="${index}"></i></div>`
			: '';

		const linkTarget = isAccessible ? 'target="_blank" rel="noopener"' : '';
		html += `
			<div class="file-item border-bottom${typeClass}" data-index="${index}" data-file-key="${escapeHtml(fileKey)}" data-file-data="${fileDataEncoded}" tabindex="0" role="button" aria-label="Open ${escapeHtml(fileName)}">
				<div class="type-rail">${showTypeLabel ? `<span class=\"type-label\"${typeTitle}>${escapeHtml(translatedType)}</span>` : ''}</div>
				<div class="file-item__lead-rails">${metaBlock}</div>
					<a href="${escapeHtml(fileHref)}" class="file-link" draggable="false" ${linkTarget}>
					<div class="d-flex gap-3 py-1 py-sm-2">
						<div class="file-info flex-grow-1">
							<div class="file-name">${highlightedFileName}</div>
							${showPath ? `<div class=\"file-path text-muted${pathHasHighlights ? ' file-path--has-highlights' : ''}\">${highlightedFilePath}</div>` : ''}
						</div>
					</div>
				</a>
				${previewOverlay}
					<div class="file-item__right-rails">
					${(isAuthenticated && isAccessible && !isRestricted) ? `<div class="tg-rail icon-rail" title="${escapeHtml(tgTitle)}"><i class="bi bi-telegram tg-send-icon${tgIconCls}" title="${escapeHtml(tgTitle)}" data-tg-href="${escapeHtml(fileHref)}"${tgIconAttr}></i></div>` : ''}
					${(isAuthenticated && isAccessible && !isRestricted) ? `<div class="fav-rail icon-rail" title="${escapeHtml(favTitle)}"><i class="bi ${favOn ? 'bi-star-fill fav-on' : 'bi-star'} fav-icon" title="${escapeHtml(favTitle)}" data-fav-href="${escapeHtml(fileHref)}" data-fav-path="${escapeHtml(favRelPath)}"></i></div>` : ''}
					${hasSummary
						? '<div class="summary-rail icon-rail" title="Show summary"><i class="bi bi-anthropic summary-icon" title="Show summary" data-action="summary"></i></div>'
						: ((isAuthenticated && isAccessible && !isRestricted)
							? '<div class="summary-rail icon-rail summary-rail--empty" aria-hidden="true"><i class="bi bi-anthropic summary-icon"></i></div>'
							: '')}
					${!isAccessible ? '<div class="public-lock-rail icon-rail" title="Login required"><span class="public-lock" title="Login required"><i class="bi bi-key-fill"></i></span></div>' : ''}
					${isRestricted ? `<div class="restriction-lock-rail icon-rail" title="Restricted file"><span class="restriction-lock" title="Restricted file"><i class="bi bi-lock" style="color: #999; opacity: 0.7;"></i></span></div>` : ''}${sizeBlock}
				</div>
			</div>
		`;
		previousType = meta.type || null;
	});

	// Append or replace content
	if (append) {
		// Append mode (infinite scroll) - no morph effect, just append
		container.append(html);
		
		// DEBUG: Log UTF-8 encoding check after DOM insertion (append mode)
		setTimeout(() => {
			const testItem = container.find('.file-item').filter((idx, el) => {
				const $el = $(el);
				const nameText = $el.find('.file-name').text();
				return nameText.toLowerCase().includes('zus') && nameText.toLowerCase().includes('tzliche');
			}).first();
			
			if (testItem.length) {
				const nameText = testItem.find('.file-name').text();
				const nameHtml = testItem.find('.file-name').html();
				console.log('[FileList Render] UTF-8 DEBUG - After DOM insertion (append):', {
					nameText: nameText,
					nameHtml: nameHtml,
					nameTextLength: nameText.length,
					nameHtmlLength: nameHtml.length,
					includesA: nameText.includes('Ä'),
					includesAumlaut: nameText.includes('\u00C4'),
					charCodes: Array.from(nameText).slice(0, 20).map(c => c.charCodeAt(0)),
					bytes: Array.from(new TextEncoder().encode(nameText)).slice(0, 20),
					domElement: testItem[0]
				});
			}
		}, 100);
		
		// Append mode - no loading state needed
	} else {
		// Replace mode - direct replacement without animation or blur
		if (debugMode === '1') {
			console.log('[FileList Render] Replacing container content:', { htmlLength: html.length, filesCount: files.length });
		}
		container.html(html);
		
		if (debugMode === '1') {
			console.log('[FileList Render] Container content replaced, DOM elements:', container.find('.file-item').length);
		}
		
		// Use requestAnimationFrame to ensure DOM is ready before attaching events
		requestAnimationFrame(() => {
			// Verify DOM was actually updated
			const actualElements = container.find('.file-item').length;
			if (debugMode === '1') {
				console.log('[FileList Render] After requestAnimationFrame:', { 
					expectedFiles: files.length, 
					actualElements, 
					containerVisible: container.is(':visible'),
					containerHeight: container.height(),
					containerDisplay: container.css('display'),
					containerOpacity: container.css('opacity')
				});
			}
			
			// Attach events to all file items
			if (window.initAllEvents && typeof window.initAllEvents === 'function') {
				window.initAllEvents(container, files, startIndex, append);
			}
			
			// НЕ обновляем счетчик здесь - это делает lazyLoadManager или другой вызывающий код
			// с правильным hasMore на основе общего количества файлов, а не размера батча
			
			// Check if container was overwritten after a delay
			if (debugMode === '1' && !append) {
				setTimeout(() => {
					const checkElements = container.find('.file-item').length;
					if (checkElements !== files.length) {
						console.warn('[FileList Render] ⚠️ Container was modified after render:', {
							expected: files.length,
							actual: checkElements,
							containerHTML: container.html().substring(0, 200)
						});
					} else {
						console.log('[FileList Render] ✅ Container unchanged after 100ms:', { elements: checkElements });
					}
				}, 100);
			}
			
			// DEBUG: Log UTF-8 encoding check after DOM insertion (replace mode)
			const testItem = container.find('.file-item').filter((idx, el) => {
				const $el = $(el);
				const nameText = $el.find('.file-name').text();
				return nameText.toLowerCase().includes('zus') && nameText.toLowerCase().includes('tzliche');
			}).first();
			
			if (testItem.length) {
				const nameText = testItem.find('.file-name').text();
				const nameHtml = testItem.find('.file-name').html();
				console.log('[FileList Render] UTF-8 DEBUG - After DOM insertion (replace):', {
					nameText: nameText,
					nameHtml: nameHtml,
					nameTextLength: nameText.length,
					nameHtmlLength: nameHtml.length,
					includesA: nameText.includes('Ä'),
					includesAumlaut: nameText.includes('\u00C4'),
					charCodes: Array.from(nameText).slice(0, 20).map(c => c.charCodeAt(0)),
					bytes: Array.from(new TextEncoder().encode(nameText)).slice(0, 20),
					domElement: testItem[0],
					computedStyle: window.getComputedStyle ? window.getComputedStyle(testItem.find('.file-name')[0]) : null
				});
			}
		});
	}
	
	return { files, startIndex, append, container };
}

export function setContainerSelector(selector) { containerSelector = selector || containerSelector; }
export function getContainerSelector() { return containerSelector; }

